import { formatNumber } from '@trmediaab/zebra-utils';
import autobind from 'class-autobind';

import BetType from 'atg/api/BetType';
import StartNumberReduction from 'atg/api/StartNumberReduction';
import * as Utils from 'atg/utils/Utils';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import { settings } from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import * as Text from 'shared/utils/Text';

import { createHelpIcon } from '../HelpIcon';
import BaseView from './BaseView';

const $ = window.$;

export default class StartNumberReductionView extends BaseView {
  constructor() {
    super(...arguments);
    autobind(this, StartNumberReductionView.prototype);
  }

  getGridClass() {
    return 'view-start-number-reduction';
  }

  // ---------------------------------------- MENU ----------------------------------------

  getViewTitle() {
    return Text.get('view-number');
  }

  getMenuTitle() {
    return Text.get('view-number');
  }

  getMenuTogglable() {
    return true;
  }

  getMenuDeletable() {
    return true;
  }

  // ---------------------------------------- GRID ----------------------------------------
  onRowWasUpdated() {}
  getGridCellInfo(position, rowCache) {
    const api = this.getAPI();

    let race;
    let betRow;
    let gameRow;
    let resultRow;
    let stakeRow;
    let nrOfBetsRow;
    let betConfig;

    if (rowCache) {
      race = rowCache.race;
      betRow = rowCache.betRow;
      gameRow = rowCache.gameRow;
      resultRow = rowCache.resultRow;
      stakeRow = rowCache.stakeRow;
      nrOfBetsRow = rowCache.nrOfBetsRow;
      betConfig = rowCache.betConfig;
    } else {
      // eslint-disable-next-line no-param-reassign
      rowCache = {};

      race = rowCache.race = api.raceInformation.getRace(position.row);
      betRow = rowCache.betRow = api.betInformation.getRow(position.row);
      gameRow = rowCache.gameRow = api.gameInformation.getRow(position.row);
      resultRow = rowCache.resultRow = api.resultInformation.getRow(
        position.row,
      );
      stakeRow = rowCache.stakeRow = api.stake.getRow(position.row);
      nrOfBetsRow = rowCache.nrOfBetsRow = api.stake.getNrOfBetsRow(
        position.row,
      );
      betConfig = rowCache.betConfig = api.betInformation.getBetConfig();
    }

    const startNumber = betRow
      ? betRow.getStartNumberAtPosition(position.column)
      : position.column + 1;

    const startIndex = startNumber - 1;
    const start = race.getStart(startIndex);

    const info = {
      rowCache: rowCache,
    };

    if (start) {
      info.number = start.getStartNumber();

      const horse = start.getHorse();
      if (horse) {
        info.horseName = horse.getName();
      }

      const jockey = start.getJockey();
      if (jockey) {
        info.jockeyTrainerName = jockey.getName();
      }

      const originalJockey = start.getOriginalJockey();
      if (originalJockey) {
        info.originalJockey = originalJockey.getName();
      }
    }

    if (
      resultRow &&
      settings.showRankingResults(getStore().getState()) &&
      betConfig.getShowResultRow(position.row)
    ) {
      info.winner = resultRow.isWinner(startIndex);
    }

    const gameRowCellInfo = this.getGameRowCellInfo(gameRow, startIndex);
    Object.assign(info, gameRowCellInfo);

    const bottomInfoParts = [];

    if (betRow) {
      const reserveNumber = betRow.getReserveNumber(startIndex);
      if (reserveNumber) {
        bottomInfoParts.push(
          `<strong class="font-extra-bold">R${reserveNumber}</strong>`,
        );
      }
    }

    if (stakeRow) {
      const percent = stakeRow.getPercentOfBets(position.column);
      if (percent) {
        const nrOfBets = nrOfBetsRow
          ? formatNumber(nrOfBetsRow.getNrOfBets(position.column))
          : undefined;
        bottomInfoParts.push(
          `${String(Math.round(percent * 1000) / 10)}%${
            nrOfBets ? ` - ${nrOfBets}` : ''
          }`,
        );
      }
    }

    info.bottomInfo =
      bottomInfoParts.length > 0
        ? $(`<span>${bottomInfoParts.join(', ')}</span>`)
        : '-';

    return info;
  }

  onGridCellTapped(position) {
    this.getGrid().highlightCell(position);

    const buttons = [
      {
        icon: 'button-last',
        onPress: this.onGridLastButtonPressed,
      },
      ...this.makeHorseButtons(position),
    ];

    this.getGrid().showCellButtons(position, buttons);
  }

  // ---------------------------------------- DATA ----------------------------------------

  getStartNumberReduction() {
    let reduction = this.getAPI()
      .betInformation.getReductionSet()
      .getStartNumberReduction();
    if (!reduction) {
      reduction = new StartNumberReduction();
    }

    return reduction;
  }

  getItem() {
    return this.getStartNumberReduction();
  }

  // ---------------------------------------- TEXT HELPER FUNCTIONS -------------------------

  isSingleRaceBetType() {
    const betTypeCode = this.getAPI().raceInformation.getBetType();
    return BetType.getBetType(betTypeCode).hasSingleRace();
  }

  getInText() {
    return this.isSingleRaceBetType()
      ? Text.get('bottom-conditions-on')
      : Text.get('bottom-conditions-in');
  }

  getInMinText() {
    return this.isSingleRaceBetType()
      ? Text.get('bottom-conditions-on-min')
      : Text.get('bottom-conditions-in-min');
  }

  getOfRacesText() {
    return this.isSingleRaceBetType()
      ? Text.get('bottom-conditions-of-positions')
      : Text.get('bottom-conditions-of-races');
  }

  getStartNumberInRaceText() {
    return this.isSingleRaceBetType()
      ? Text.get('bottom-conditions-startnumber-on-position')
      : Text.get('bottom-conditions-startnumber-in-race');
  }

  // ---------------------------------------- BOTTOM INFO DELEGATE METHODS ----------------------------------------

  getBottomInfoContent() {
    const $conditions = (this.$conditions = StickyTemplate.jq(
      'bottom-conditions-start-number-reduction',
      {
        /*** Section 1 ***/
        '%start-number-reduction-sum%': Text.get(
          'bottom-conditions-start-number-reduction-sum',
        ),
        /*** Section 2 ***/
        '%start-number-reduction-number-winners%': Text.get(
          'bottom-conditions-start-number-reduction-number-winners',
        ),
        /*** Section 3 ***/
        '%start-number-reduction-miscellaneous%': Text.get(
          'bottom-conditions-start-number-reduction-miscellaneous',
        ),
      },
    ));

    $conditions.find('.point-sums h2').append(
      createHelpIcon({
        header: Text.get('help-startnumbers-reduction-points-header'),
        body: Text.get('help-startnumbers-reduction-points-body'),
      }),
    );

    $conditions.find('.winners-in-intervals h2').append(
      createHelpIcon({
        header: Text.get('help-startnumbers-reduction-intervals-header'),
        body: Text.get('help-startnumbers-reduction-intervals-body'),
      }),
    );

    $conditions.find('.misc h2').append(
      createHelpIcon({
        header: Text.get('help-startnumbers-reduction-misc-header'),
        body: Text.get('help-startnumbers-reduction-misc-body'),
      }),
    );

    $conditions.find('form').event('submit', this.onFormSubmit);

    this.updateConditionsPointSums();
    this.updateConditionsWinnersInIntervals();
    this.updateConditionsMisc();

    return $conditions;
  }

  // ---------------------------------------- POINT SUMS ----------------------------------------

  updateConditionsPointSums() {
    const reduction = this.getStartNumberReduction();
    const numRaces = this.getAPI().raceInformation.getNumberOfRaces();
    const numberOfRows = Math.max(1, reduction.getNumberOfPointSums());

    const $wrapper = this.$conditions.find('.point-sums');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    for (let n = 0; n < numberOfRows; n++) {
      const pointSum = reduction.getPointSum(n);

      const $row = StickyTemplate.jq(
        'bottom-conditions-start-number-reduction-point-sum-row',
        {
          '%Min%': Text.get('bottom-conditions-Min'),
          '%max%': Text.get('bottom-conditions-max'),
          '%in-point-sum%': Text.get(
            'bottom-conditions-start-number-reduction-in-point-sum',
          ),
          '%in%': this.getInText(),
          '%of-races%': this.getOfRacesText(),
          '%num-races%': numRaces,
          '%row%': n,
        },
      );

      this.addNumberOfRacesInput($row, 1);

      let legNumbers;

      if (pointSum) {
        $row
          .find('.min-points')
          .val(Utils.negativeBlankToValue(pointSum.getMinPoints()));
        $row
          .find('.max-points')
          .val(Utils.negativeBlankToValue(pointSum.getMaxPoints()));
        $row
          .find('.number-of-races')
          .val(pointSum.getNumberOfRaces() || numRaces);

        legNumbers = pointSum.getLegNumbers();
      } else {
        $row.find('.number-of-races').val(numRaces);
        legNumbers = this.getLegNumbers();
      }

      this.addLegNumbersInputs($row, legNumbers);
      $rows.append($row);
    }

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);
    $wrapper.find('.button.plus').reevent('click', this.onAddPointSumPressed);
    $wrapper
      .find('.button.delete')
      .reevent('click', this.onDeletePointSumPressed);

    this.attachNegativeBlankEvents($wrapper);
  }

  onAddPointSumPressed() {
    const reduction = this.getStartNumberReduction();
    let pointSum;

    if (reduction.getNumberOfPointSums() === 0) {
      pointSum = reduction.addPointSum();
      pointSum.setLegNumbers(this.getLegNumbers());
    }

    pointSum = reduction.addPointSum();
    pointSum.setLegNumbers(this.getLegNumbers());

    this.getAPI().scheduleBetUpdate();

    this.updateConditionsPointSums();

    const $row = this.$conditions.find('.point-sums .rows .row:last-child');
    $row.hide();
    $row.slideDown(300);
  }

  onDeletePointSumPressed(event) {
    const $target = $(event.currentTarget);
    const $row = $target.closest('.row');
    const index = $row.index();

    const reduction = this.getStartNumberReduction();
    reduction.deletePointSum(index);
    this.getAPI().scheduleBetUpdate();

    if (reduction.getNumberOfPointSums() < 1) {
      this.updateConditionsPointSums();
    } else {
      $row.slideUp(
        300,
        function () {
          this.updateConditionsPointSums();
        }.bind(this),
      );
    }
  }

  // ---------------------------------------- WINNERS IN INTERVALS ----------------------------------------

  updateConditionsWinnersInIntervals() {
    const reduction = this.getStartNumberReduction();
    const numberOfRows = Math.max(1, reduction.getNumberOfWinnersInIntervals());

    const $wrapper = this.$conditions.find('.winners-in-intervals');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    for (let n = 0; n < numberOfRows; n++) {
      const winnersInInterval = reduction.getWinnersInInterval(n);

      const $row = StickyTemplate.jq(
        'bottom-conditions-start-number-reduction-winners-in-interval-row',
        {
          '%From-start-number%': Text.get(
            'bottom-conditions-start-number-reduction-From-start-number',
          ),
          '%to%': Text.get('bottom-conditions-to'),
          '%points%': Text.get('bottom-conditions-points'),
          '%in-min%': this.getInMinText(),
          '%max%': Text.get('bottom-conditions-max'),
          '%of-races%': this.getOfRacesText(),
          '%startnumber-in-race%': this.getStartNumberInRaceText(),
          '%row%': n,
        },
      );

      this.addNumberOfRacesInput($row);

      let legNumbers;

      if (winnersInInterval) {
        $row
          .find('.from-start-number')
          .val(
            Utils.negativeBlankToValue(
              winnersInInterval.getFromStartNumber(),
              0,
            ),
          );
        $row
          .find('.to-start-number')
          .val(
            Utils.negativeBlankToValue(winnersInInterval.getToStartNumber(), 0),
          );
        $row
          .find('.min-number-of-races')
          .val(winnersInInterval.getMinNumberOfRaces());
        $row
          .find('.max-number-of-races')
          .val(winnersInInterval.getMaxNumberOfRaces());
        legNumbers = winnersInInterval.getLegNumbers();
      } else {
        legNumbers = this.getLegNumbers();
      }

      this.addLegNumbersInputs($row, legNumbers);

      $rows.append($row);
    }

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);
    $wrapper
      .find('.button.plus')
      .reevent('click', this.onAddWinnersInIntervalPressed);
    $wrapper
      .find('.button.delete')
      .reevent('click', this.onDeleteWinnersInIntervalPressed);

    this.attachNegativeBlankEvents($wrapper);
  }

  onAddWinnersInIntervalPressed() {
    const reduction = this.getStartNumberReduction();
    let winnersInInterval;

    if (reduction.getNumberOfWinnersInIntervals() === 0) {
      winnersInInterval = reduction.addWinnersInInterval();
      winnersInInterval.setLegNumbers(this.getLegNumbers());
    }

    winnersInInterval = reduction.addWinnersInInterval();
    winnersInInterval.setLegNumbers(this.getLegNumbers());

    this.getAPI().scheduleBetUpdate();

    this.updateConditionsWinnersInIntervals();

    const $row = this.$conditions.find(
      '.winners-in-intervals .rows .row:last-child',
    );
    $row.hide();
    $row.slideDown(300);
  }

  onDeleteWinnersInIntervalPressed(event) {
    const $target = $(event.currentTarget);
    const $row = $target.closest('.row');
    const index = $row.index();

    const reduction = this.getStartNumberReduction();
    reduction.deleteWinnersInInterval(index);
    this.getAPI().scheduleBetUpdate();

    if (reduction.getNumberOfWinnersInIntervals() < 1) {
      this.updateConditionsWinnersInIntervals();
    } else {
      $row.slideUp(
        300,
        function () {
          this.updateConditionsWinnersInIntervals();
        }.bind(this),
      );
    }
  }

  // ---------------------------------------- MISC ----------------------------------------

  updateConditionsMisc() {
    const reduction = this.getStartNumberReduction();
    const numberOfRows = Math.max(1, reduction.getNumberOfMiscs());

    const $wrapper = this.$conditions.find('.misc');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    for (let n = 0; n < numberOfRows; n++) {
      const misc = reduction.getMisc(n);

      const $row = StickyTemplate.jq(
        'bottom-conditions-start-number-reduction-misc-row',
        {
          '%from%': Text.get('bottom-conditions-from'),
          '%to%': Text.get('bottom-conditions-to'),
          '%startnumber-in-race%': this.getStartNumberInRaceText(),
          '%row%': n,
        },
      );

      this.addMiscRequireModeInput($row);
      this.addMiscTypeInput($row);

      let legNumbers;

      if (misc) {
        $row
          .find('.from-points')
          .val(Utils.negativeBlankToValue(misc.getFromPoints()));
        $row
          .find('.to-points')
          .val(Utils.negativeBlankToValue(misc.getToPoints()));

        $row.find('.misc-require-mode').val(misc.getRequireMode());
        $row.find('.misc-type').val(misc.getType());

        legNumbers = misc.getLegNumbers();
      } else {
        legNumbers = this.getLegNumbers();
      }

      this.addLegNumbersInputs($row, legNumbers);

      $rows.append($row);
    }

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);
    $wrapper.find('.button.plus').reevent('click', this.onAddMiscPressed);
    $wrapper.find('.button.delete').reevent('click', this.onDeleteMiscPressed);

    this.attachNegativeBlankEvents($wrapper);
  }

  onAddMiscPressed() {
    const reduction = this.getStartNumberReduction();
    let misc;

    if (reduction.getNumberOfMiscs() === 0) {
      misc = reduction.addMisc();
      misc.setLegNumbers(this.getLegNumbers());
    }

    misc = reduction.addMisc();
    misc.setLegNumbers(this.getLegNumbers());

    this.getAPI().scheduleBetUpdate();

    this.updateConditionsMisc();

    const $row = this.$conditions.find('.misc .rows .row:last-child');
    $row.hide();
    $row.slideDown(300);
  }

  onDeleteMiscPressed(event) {
    const $target = $(event.currentTarget);
    const $row = $target.closest('.row');
    const index = $row.index();

    const reduction = this.getStartNumberReduction();

    reduction.deleteMisc(index);
    this.getAPI().scheduleBetUpdate();

    if (reduction.getNumberOfMiscs() < 1) {
      this.updateConditionsMisc();
    } else {
      $row.slideUp(
        300,
        function () {
          this.updateConditionsMisc();
        }.bind(this),
      );
    }
  }

  // ---------------------------------------- FORM TO DATA ----------------------------------------

  onConditionsChanged() {
    const $form = this.$conditions.find('form');
    const formData = $form.formData();
    const reduction = this.getStartNumberReduction();
    const numberOfPointSums = reduction.getNumberOfPointSums();
    let numberOfFormRows = formData.pointSums.length;
    let n;

    for (n = 0; n < numberOfFormRows; n++) {
      if (n >= numberOfPointSums) {
        reduction.addPointSum();
      }

      const pointSum = reduction.getPointSum(n);
      const pointSumData = formData.pointSums[n];

      pointSum.setMinPoints(Utils.valueToNegativeBlank(pointSumData.minPoints));
      pointSum.setMaxPoints(Utils.valueToNegativeBlank(pointSumData.maxPoints));
      pointSum.setNumberOfRaces(pointSumData.numberOfRaces);
      pointSum.setLegNumbers(pointSumData.legNumbers);
    }

    numberOfFormRows = formData.winnersInIntervals.length;
    const numberOfWinnersInIntervals =
      reduction.getNumberOfWinnersInIntervals();

    for (n = 0; n < numberOfFormRows; n++) {
      if (n >= numberOfWinnersInIntervals) {
        reduction.addWinnersInInterval();
      }

      const winnersInInterval = reduction.getWinnersInInterval(n);
      const winnersInIntervalData = formData.winnersInIntervals[n];

      winnersInInterval.setFromStartNumber(
        Utils.valueToNegativeBlank(winnersInIntervalData.fromStartNumber),
      );
      winnersInInterval.setToStartNumber(
        Utils.valueToNegativeBlank(winnersInIntervalData.toStartNumber),
      );
      winnersInInterval.setMaxNumberOfRaces(
        winnersInIntervalData.maxNumberOfRaces,
      );
      winnersInInterval.setMinNumberOfRaces(
        winnersInIntervalData.minNumberOfRaces,
      );
      winnersInInterval.setLegNumbers(winnersInIntervalData.legNumbers);
    }

    numberOfFormRows = formData.misc.length;
    const numberOfMiscs = reduction.getNumberOfMiscs();

    for (n = 0; n < numberOfFormRows; n++) {
      if (n >= numberOfMiscs) {
        reduction.addMisc();
      }

      const misc = reduction.getMisc(n);
      const miscData = formData.misc[n];

      misc.setFromPoints(Utils.valueToNegativeBlank(miscData.fromPoints));
      misc.setToPoints(Utils.valueToNegativeBlank(miscData.toPoints));
      misc.setRequireMode(miscData.requireMode);
      misc.setType(miscData.type);
      misc.setLegNumbers(miscData.legNumbers);
    }

    this.getAPI().scheduleBetUpdate();
  }
}
